import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { fetchProjectGoals, createGoal, updateGoal, deleteGoal, restoreGoal } from '../../store/slices/goalsSlice';
import { fetchProjectMilestones } from '../../store/slices/milestonesSlice';
import LoadingSpinner from '../UI/LoadingSpinner';
import Button from '../UI/Button';
import { Goal } from './Goals/index';
import { InlineEditGoal } from '../UI/InlineEdit';
import { Milestone } from './Milestones/index';
import GoalForm from './Goals/GoalForm';
import { GoalFormData } from './Goals/index';

interface ProjectGoalsProps {
  projectId?: string;
  milestones?: Milestone[];
  onGoalUpdate?: (goalId: string, data: Partial<Goal> & { updateNotes?: string }) => Promise<void> | void;
  disabled?: boolean;
  isOwner?: boolean;
}

const ProjectGoals: React.FC<ProjectGoalsProps> = ({ 
  projectId: propProjectId,
  milestones = [],
  onGoalUpdate,
  disabled = false,
  isOwner = false,
}) => {
  const { id: paramProjectId } = useParams<{ id: string }>();
  const projectId = propProjectId || paramProjectId || '';
  const dispatch = useAppDispatch();
  const { items, projectGoals, isLoading } = useAppSelector((state) => state.goals);
  const { items: milestoneItems, projectMilestones: projectMilestonesMap } = useAppSelector((state) => state.milestones);
  const [showAddForm, setShowAddForm] = useState(false);
  const [goalForm, setGoalForm] = useState<Partial<GoalFormData>>({});

  useEffect(() => {
    if (projectId) {
      dispatch(fetchProjectGoals({ projectId, includeDeleted: isOwner }));
      dispatch(fetchProjectMilestones({ projectId, includeDeleted: isOwner }));
    }
  }, [dispatch, projectId, isOwner]);

  // Get milestones for this project (from API or fallback to prop)
  const projectMilestonesList = projectId && projectMilestonesMap[projectId]
    ? projectMilestonesMap[projectId].map((id) => milestoneItems[id]).filter(Boolean) as Milestone[]
    : milestones; // Fallback to prop if available

  const allGoals = projectId && projectGoals[projectId]
    ? projectGoals[projectId].map((id) => items[id]).filter(Boolean) as Goal[]
    : [];

  // Filter: show deleted only to owners, hide from others
  const activeGoals = allGoals.filter((g) => !g.deletedAt);
  const deletedGoals = isOwner ? allGoals.filter((g) => g.deletedAt) : [];

  const handleGoalUpdate = async (goalId: string, data: Partial<Goal> & { updateNotes?: string }) => {
    if (onGoalUpdate) {
      await onGoalUpdate(goalId, data);
    } else {
      // Default: use Redux action
      await dispatch(updateGoal({ id: goalId, data }));
    }
  };

  const handleAddGoal = async () => {
    if (!goalForm.title || !goalForm.targetValue || !goalForm.unit || !projectId) return;
    
    try {
      const goalData: Partial<Goal> & { projectId: string } = {
        projectId,
        title: goalForm.title!,
        description: goalForm.description,
        targetValue: goalForm.targetValue!,
        currentValue: goalForm.currentValue || 0,
        unit: goalForm.unit!,
        status: goalForm.status || 'pending',
        milestoneId: goalForm.milestoneId || undefined,
      };
      await dispatch(createGoal(goalData));
      setShowAddForm(false);
      setGoalForm({});
    } catch (error) {
      console.error('Error creating goal:', error);
    }
  };

  const handleDeleteGoal = async (goalId: string) => {
    if (!confirm('Are you sure you want to remove this goal?')) return;
    try {
      await dispatch(deleteGoal(goalId));
    } catch (error) {
      console.error('Error deleting goal:', error);
    }
  };

  const handleRestoreGoal = async (goalId: string) => {
    try {
      await dispatch(restoreGoal(goalId));
    } catch (error) {
      console.error('Error restoring goal:', error);
    }
  };

  if (isLoading) {
    return <LoadingSpinner size="sm" text="Loading goals..." />;
  }

  return (
    <div className="space-y-6">
      {!disabled && isOwner && (
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Goals</h3>
          {!showAddForm && (
            <Button
              type="button"
              size="sm"
              onClick={() => setShowAddForm(true)}
            >
              Add Goal
            </Button>
          )}
        </div>
      )}

      {showAddForm && !disabled && isOwner && (
        <div className="border rounded-lg p-4 bg-gray-50">
          <GoalForm
            formData={{
              title: goalForm.title || '',
              description: goalForm.description,
              targetValue: goalForm.targetValue || 0,
              currentValue: goalForm.currentValue || 0,
              unit: goalForm.unit || '',
              status: goalForm.status || 'pending',
              milestoneId: goalForm.milestoneId,
            } as GoalFormData}
            onFormDataChange={(data) => setGoalForm((prev) => ({ ...prev, ...data }))}
            milestones={projectMilestonesList}
            onSubmit={handleAddGoal}
            onCancel={() => {
              setShowAddForm(false);
              setGoalForm({});
            }}
            submitLabel="Add"
          />
        </div>
      )}

      {activeGoals.length === 0 && deletedGoals.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No goals defined</p>
        </div>
      ) : (
        <>
          {activeGoals.length > 0 && (
            <div className="space-y-4">
              {activeGoals.map((goal) => {
                const goalId = goal._id || goal.id || '';
                return (
                  <div key={goalId} className="relative group">
                    <InlineEditGoal
                      goal={goal}
                      onSave={(data) => handleGoalUpdate(goalId, data)}
                      disabled={disabled}
                      milestones={projectMilestonesList}
                    />
                    {!disabled && isOwner && (
                      <button
                        type="button"
                        onClick={() => handleDeleteGoal(goalId)}
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-red-50 hover:bg-red-100 text-red-600 rounded-full p-1"
                        aria-label="Remove goal"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {deletedGoals.length > 0 && isOwner && (
            <div className="mt-8 border-t pt-6">
              <h4 className="text-sm font-medium text-gray-500 mb-4">Removed Goals</h4>
              <div className="space-y-3">
                {deletedGoals.map((goal) => {
                  const goalId = goal._id || goal.id || '';
                  return (
                    <div
                      key={goalId}
                      className="border rounded-lg p-4 bg-gray-50 opacity-60 relative"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-700 line-through">{goal.title}</h4>
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Removed</span>
                          </div>
                          {goal.description && (
                            <p className="text-sm text-gray-500 mt-1 line-through">{goal.description}</p>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestoreGoal(goalId)}
                        >
                          Restore
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProjectGoals;

